import { useMemo } from 'react';
import { CHART_STYLE, formatDataValue, analyzeDecimals } from './chartStyles';
import type { ChartConfig } from '../../../types';

interface BoxPlotViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

interface BoxStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

/**
 * Box plot — shows distribution of a single numeric column (Rule 5b: rows ≤ 50).
 * Rendered as a custom SVG since Recharts doesn't have a native box plot.
 */
export function BoxPlotView({ data, config }: BoxPlotViewProps) {
  const key = config.xAxis ?? '';
  const { hasDecimals, allSmall } = analyzeDecimals(data, key);

  const stats: BoxStats | null = useMemo(() => {
    const values = data
      .map((row) => row[key])
      .filter((v): v is number => typeof v === 'number' && isFinite(v))
      .sort((a, b) => a - b);

    if (values.length < 2) return null;

    return {
      min: values[0],
      q1: quantile(values, 0.25),
      median: quantile(values, 0.5),
      q3: quantile(values, 0.75),
      max: values[values.length - 1],
    };
  }, [data, key]);

  if (!stats) return <p className="text-xs text-gray-500">Not enough data for a box plot.</p>;

  // SVG dimensions
  const W = 400;
  const H = 200;
  const pad = { top: 30, bottom: 30, left: 60, right: 40 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const range = stats.max - stats.min || 1;
  const x = (val: number) => pad.left + ((val - stats.min) / range) * plotW;
  const midY = pad.top + plotH / 2;
  const boxH = plotH * 0.5;

  const fmt = (v: number) => formatDataValue(v, hasDecimals, allSmall);

  return (
    <div className="flex justify-center">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md" style={{ height: 200 }}>
        {/* Whisker line (min to max) */}
        <line x1={x(stats.min)} y1={midY} x2={x(stats.max)} y2={midY} stroke="#9CA3AF" strokeWidth={1} />

        {/* Min whisker cap */}
        <line x1={x(stats.min)} y1={midY - boxH / 4} x2={x(stats.min)} y2={midY + boxH / 4} stroke="#6B7280" strokeWidth={1.5} />

        {/* Max whisker cap */}
        <line x1={x(stats.max)} y1={midY - boxH / 4} x2={x(stats.max)} y2={midY + boxH / 4} stroke="#6B7280" strokeWidth={1.5} />

        {/* Box (Q1 to Q3) */}
        <rect
          x={x(stats.q1)}
          y={midY - boxH / 2}
          width={x(stats.q3) - x(stats.q1)}
          height={boxH}
          fill={CHART_STYLE.colors[0]}
          fillOpacity={0.2}
          stroke={CHART_STYLE.colors[0]}
          strokeWidth={1.5}
          rx={3}
        />

        {/* Median line */}
        <line
          x1={x(stats.median)}
          y1={midY - boxH / 2}
          x2={x(stats.median)}
          y2={midY + boxH / 2}
          stroke={CHART_STYLE.colors[0]}
          strokeWidth={2}
        />

        {/* Labels */}
        <text x={x(stats.min)} y={midY + boxH / 2 + 16} textAnchor="middle" fontSize={CHART_STYLE.fontSize.dataLabel} fill="#6B7280">{fmt(stats.min)}</text>
        <text x={x(stats.q1)} y={midY - boxH / 2 - 6} textAnchor="middle" fontSize={CHART_STYLE.fontSize.dataLabel} fill="#6B7280">Q1: {fmt(stats.q1)}</text>
        <text x={x(stats.median)} y={midY - boxH / 2 - 6} textAnchor="middle" fontSize={CHART_STYLE.fontSize.dataLabel} fill={CHART_STYLE.colors[0]} fontWeight={600}>Med: {fmt(stats.median)}</text>
        <text x={x(stats.q3)} y={midY + boxH / 2 + 16} textAnchor="middle" fontSize={CHART_STYLE.fontSize.dataLabel} fill="#6B7280">Q3: {fmt(stats.q3)}</text>
        <text x={x(stats.max)} y={midY + boxH / 2 + 16} textAnchor="middle" fontSize={CHART_STYLE.fontSize.dataLabel} fill="#6B7280">{fmt(stats.max)}</text>

        {/* Column name */}
        <text x={W / 2} y={14} textAnchor="middle" fontSize={CHART_STYLE.fontSize.chartTitle} fill="#374151" fontWeight={600}>{key}</text>
      </svg>
    </div>
  );
}
